import React, {useContext, useEffect, useMemo, useRef} from 'react';
import {Navigate, useLocation} from "react-router-dom";
import {gql, useQuery} from "@apollo/client";
import {getToken, getTokenVariables} from "./types/getToken";
import {OAuthContext} from "./Provider";
import Progress from '../../../util/Progress';
import CenteredNotification from "../../../util/CenteredNotification";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {ExclamationTriangleIcon} from "@radix-ui/react-icons";

const OAUTH_CLIENT_ID = gql`query getToken($code: ID!) {
    qqcatalyst {
        token(code: $code) {
            accessToken
            refreshToken
            tokenType
            expiresIn
        }
    }
}`;

function getParameterByName(url: string, name: string): string {
    let match = RegExp('[?&]' + name + '=([^&]*)').exec(url);
    return (match) ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : "";
}

export default function OAuthCallback() {
    const {setToken} = useContext(OAuthContext);
    const location = useLocation();
    const code = useMemo(() => getParameterByName(location.search, 'code'), [ location.search ]);
    const fired = useRef(false);

    const { loading, data, error } = useQuery<getToken, getTokenVariables>(OAUTH_CLIENT_ID, { variables: { code }});

    useEffect(() => {
        if (fired.current) return;
        if (!loading && !error && data?.qqcatalyst?.token) {
            fired.current = true;
            setToken(data.qqcatalyst.token);
        }
    }, [loading, data, error, setToken]);

    if (loading) {
        return <Progress>Loading ...</Progress>
    }

    if (error) {
        return (
            <CenteredNotification>
                <Alert variant="destructive">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <AlertTitle>Authorization failed</AlertTitle>
                    <AlertDescription>Error fetching token: {error.message}</AlertDescription>
                </Alert>
            </CenteredNotification>
        );
    }

    if (data?.qqcatalyst?.token) {
        return <Navigate to={"/qqcatalyst"} replace />
    }

    return <Progress>Initializing ...</Progress>
}
